// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { type ReactNode, useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const LOADER_DURATION_MS = 300

export default function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [loading, setLoading] = useState(false)
  const isFirst = useRef(true)

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }
    setLoading(true)
    const t = setTimeout(() => setLoading(false), LOADER_DURATION_MS)
    return () => clearTimeout(t)
  }, [pathname])

  return (
    <>
      {/* Brand-gradient top bar */}
      <div className="fixed top-0 left-0 right-0 z-[999] h-[4px] overflow-hidden pointer-events-none">
        <div
          key={pathname}
          style={{
            height: '100%',
            width: '100%',
            background: 'linear-gradient(90deg, #006EF9 0%, #02C7C3 60%, #6DD5FA 100%)',
            boxShadow: '0 0 10px 2px rgba(2,199,195,0.7)',
            animation: 'qh-route-bar 600ms cubic-bezier(0.65,0,0.35,1) both',
          }}
        />
      </div>

      {loading ? (
        <PageLoader />
      ) : (
        <div
          key={pathname + '-c'}
          className="flex flex-col flex-1"
          style={{ animation: 'qh-page-enter 380ms cubic-bezier(0.16,1,0.3,1) both' }}
        >
          {children}
        </div>
      )}
    </>
  )
}

function PageLoader() {
  return (
    <div className="flex flex-col flex-1 items-center justify-center bg-background">
      <div className="relative h-11 w-11">
        <div className="absolute inset-0 rounded-full border-2 border-border" />
        <div
          className="absolute inset-0 rounded-full border-[3px] border-transparent animate-spin"
          style={{
            borderTopColor: '#006EF9',
            borderRightColor: '#02C7C3',
            animationDuration: '0.8s',
          }}
        />
      </div>
    </div>
  )
}
